import { Project, endpoints, paginated, type ProjectUpdate } from '@gopinhos/shared';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';

import { apiClient } from '../lib/api-client';

const ProjectListResponse = paginated(Project.partial({ archivedAt: true }));

export function useProjects(): UseQueryResult<z.infer<typeof ProjectListResponse>> {
  return useQuery({
    queryKey: ['projects'],
    queryFn: () =>
      apiClient.request({
        method: 'GET',
        path: endpoints.projects(),
        response: ProjectListResponse,
      }),
  });
}

export function useProject(id: string | undefined): UseQueryResult<unknown> {
  return useQuery({
    queryKey: ['projects', id],
    enabled: !!id,
    queryFn: () =>
      apiClient.request({
        method: 'GET',
        path: endpoints.project(id!),
      }),
  });
}

export function useUpdateProject(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProjectUpdate) =>
      apiClient.request({
        method: 'PATCH',
        path: endpoints.project(id),
        body: input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects', id] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}
